import {
  DEFAULT_DOOZ_SEAT0_COLOR,
  DOOZ_PIECE_COLORS,
  doozSeatColor,
  otherDoozColor,
  type DoozPieceColor,
} from "@bing/shared";

/**
 * دوز's piece palette.
 *
 * ⚠️ THE COLOUR BELONGS TO A SEAT, AND THERE ARE EXACTLY TWO (operator, 2026-09-03).
 *
 * It used to be VIEWER-SIDE with four options — each player rendered their own pieces in whatever
 * they had picked and the opponent's in the contrasting one — on the reasoning that the colour
 * carries no rule, so nothing had to be agreed. What that actually bought was two players looking
 * at the same cell and seeing different colours, four options for a two-player game, and no way to
 * say «the red one» out loud. It is a shared fact now, like شطرنج's: one colour is SEAT 0's, the
 * other seat takes the remaining one, and both screens draw the same board.
 *
 *   • PUBLIC — seat 0 (whoever created the table) takes {@link DEFAULT_DOOZ_SEAT0_COLOR}; nobody
 *     is asked, because there is nobody to ask for the stranger who joins.
 *   • PRIVATE — the HOST picks, in «تنظیمات میز». They are seat 0, so their pick IS seat 0's
 *     colour and it rides the table settings to both clients.
 *   • SOLO — the human is seat 0 and picks on the start screen.
 *
 * The rule itself ({@link doozSeatColor}) lives in `@bing/shared` beside the settings schema that
 * carries it, so the server's own normalisation and this board cannot drift apart.
 */
export {
  DEFAULT_DOOZ_SEAT0_COLOR,
  DOOZ_PIECE_COLORS,
  doozSeatColor,
  otherDoozColor,
  type DoozPieceColor,
};

export interface DoozTone {
  /** The disc's lit face. */
  readonly light: string;
  /** Its body. */
  readonly base: string;
  /** The rim, and the colour a preview ghost is tinted with. */
  readonly deep: string;
}

/**
 * The two tones, keyed to the app's own board-game palette (brass, felt, obsidian) rather than to
 * flat primaries — a Connect Four board in this app should look like it belongs beside شطرنج and
 * تخته نرد, not like a different product.
 *
 * ⚠️ THESE ARE THE ARTWORK'S OWN VALUES. `apps/web/tools/dooz-art/PROMPTS.md` renders the banner
 * and the lobby tile from this same table, so the discs on the board and the discs in the picture
 * are the same two colours. Change one and the board stops matching its own artwork.
 *
 * Yellow is the only LIGHT tone, so the two sides always differ in lightness as well as in hue —
 * which is what keeps them apart for a colour-blind player, and what a red/green pair would not
 * have given.
 */
export const DOOZ_TONES: Readonly<Record<DoozPieceColor, DoozTone>> = {
  red: { light: "#ff8b73", base: "#d6402c", deep: "#7c1d12" },
  yellow: { light: "#ffe08a", base: "#e5aa1e", deep: "#8a5f06" },
};

export function doozTone(color: DoozPieceColor): DoozTone {
  return DOOZ_TONES[color];
}
